import {
    ADDRESS_CLAMP_TO_EDGE,
    BLENDEQUATION_ADD,
    BLENDMODE_ONE,
    BLENDMODE_ZERO,
    BLENDMODE_ONE_MINUS_SRC_ALPHA,
    BlendState,
    Color,
    FILTER_NEAREST,
    GraphicsDevice,
    RenderPassPicker,
    RenderTarget,
    Texture
} from 'playcanvas';

import { ElementType } from './element';
import { Scene } from './scene';
import { Splat } from './splat';

const idClearColor = new Color(1, 1, 1, 1);

// result of an id readback: ids row-major over the pixel rectangle read
// (x, y, width, height in render-target pixels) and the target's size then
type IdPick = {
    ids: number[],
    x: number,
    y: number,
    width: number,
    height: number,
    targetWidth: number,
    targetHeight: number
};
const depthClearColor = new Color(0, 0, 0, 1);

// Shared buffer for half-to-float conversion
const float32 = new Float32Array(1);
const uint32 = new Uint32Array(float32.buffer);

// Convert 16-bit half-float to 32-bit float using bit manipulation
const half2Float = (h: number): number => {
    const sign = (h & 0x8000) << 16;           // Move sign to bit 31
    const exponent = (h & 0x7C00) >> 10;       // Extract 5-bit exponent
    const mantissa = h & 0x03FF;               // Extract 10-bit mantissa

    if (exponent === 0) {
        if (mantissa === 0) {
            // Zero
            uint32[0] = sign;
        } else {
            // Denormalized: convert to normalized float32
            let e = -1;
            let m = mantissa;
            do {
                e++;
                m <<= 1;
            } while ((m & 0x0400) === 0);
            uint32[0] = sign | ((127 - 15 - e) << 23) | ((m & 0x03FF) << 13);
        }
    } else if (exponent === 31) {
        // Infinity or NaN
        uint32[0] = sign | 0x7F800000 | (mantissa << 13);
    } else {
        // Normalized: adjust exponent bias from 15 to 127
        uint32[0] = sign | ((exponent + 127 - 15) << 23) | (mantissa << 13);
    }

    return float32[0];
};

class Picker {
    private device: GraphicsDevice;
    private scene: Scene;

    // Render targets (provided by camera)
    private depthRenderTarget: RenderTarget | null = null;
    private idRenderTarget: RenderTarget | null = null;

    // Render pass (shared for depth and ID picking)
    private renderPass: RenderPassPicker;

    // Blend state for depth accumulation
    private depthBlendState: BlendState;

    constructor(scene: Scene) {
        this.scene = scene;
        this.device = scene.graphicsDevice;

        // Create shared render pass for picking
        this.renderPass = new RenderPassPicker(this.device, this.scene.app.renderer);

        // Blend state for depth accumulation:
        // RGB: additive depth accumulation (ONE, ONE_MINUS_SRC_ALPHA)
        // Alpha: multiplicative transmittance (ZERO, ONE_MINUS_SRC_ALPHA) -> T = T * (1 - alpha)
        this.depthBlendState = new BlendState(
            true,
            BLENDEQUATION_ADD, BLENDMODE_ONE, BLENDMODE_ONE_MINUS_SRC_ALPHA,           // RGB blend
            BLENDEQUATION_ADD, BLENDMODE_ZERO, BLENDMODE_ONE_MINUS_SRC_ALPHA           // Alpha blend (transmittance)
        );
    }

    // Set render targets from camera
    setRenderTargets(depthRT: RenderTarget, idRT: RenderTarget) {
        this.depthRenderTarget = depthRT;
        this.idRenderTarget = idRT;
    }

    // A queued gesture may outlive a viewport resize. Borrow the camera target
    // at matching sizes; otherwise keep a private target until readback settles.
    async withTargetSize<T>(kind: 'id' | 'depth', size: { width: number, height: number }, fn: () => Promise<T>): Promise<T> {
        const key = kind === 'id' ? 'idRenderTarget' : 'depthRenderTarget';
        const liveTarget = this[key];
        if (!liveTarget || (liveTarget.width === size.width && liveTarget.height === size.height)) {
            return fn();
        }

        const target = new RenderTarget({
            colorBuffer: new Texture(this.device, {
                name: `pick-${kind}`,
                width: size.width,
                height: size.height,
                format: liveTarget.colorBuffer.format,
                mipmaps: false,
                minFilter: FILTER_NEAREST,
                magFilter: FILTER_NEAREST,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE
            }),
            depth: false,
            autoResolve: false
        });
        this[key] = target;
        try {
            return await fn();
        } finally {
            this[key] = liveTarget;
            target.destroyTextureBuffers();
            target.destroy();
        }
    }

    // Prepare for ID picking by rendering the specified splat. footprint is the
    // gesture's selection footprint, passed in rather than read live
    prepareId(splat: Splat, mode: 'add' | 'remove' | 'set' | 'intersect', footprint: number) {
        if (!this.idRenderTarget) {
            return;
        }

        // the id pass draws the compact list through the sort, so it needs a
        // current sorted projection: a stochastic frame leaves sortedIndices
        // stale (old camera, old survivor set)
        this.scene.projectedSplatRenderer.renderSortedForPick(this.idRenderTarget);

        const { splatLayer } = this.scene;

        // Hide non-selected elements
        const splats = this.scene.getElementsByType(ElementType.splat) as Splat[];
        splats.forEach((s) => {
            s.entity.enabled = s === splat;
        });

        // 'intersect' picks against the currently-selected set (same render as
        // 'remove') so unselected splats can't occlude selected ones and skew it.
        const pickOp = mode === 'intersect' ? 'remove' : mode;
        const pickOpIndex = ['add', 'remove', 'set'].indexOf(pickOp);

        // Set picker uniforms
        this.device.scope.resolve('pickOp').setValue(pickOpIndex);
        this.device.scope.resolve('pickMode').setValue(0);
        this.scene.projectedSplatRenderer.preparePick(splat, pickOpIndex, false, footprint);

        // Render ID picking pass
        const emptyMap = new Map();
        this.renderPass.blendState = BlendState.NOBLEND;
        this.renderPass.init(this.idRenderTarget);
        this.renderPass.setClearColor(idClearColor);
        this.renderPass.update(this.scene.camera.camera, this.scene.app.scene, [splatLayer], emptyMap, false);
        this.renderPass.render();
        this.scene.projectedSplatRenderer.finishPick();

        // Re-enable all splats
        splats.forEach((s) => {
            s.entity.enabled = true;
        });
    }

    // Read single splat ID at normalized screen position (after prepareId)
    async readId(x: number, y: number): Promise<number> {
        if (!this.idRenderTarget) {
            return -1;
        }
        // For single pixel read, use a minimal normalized size
        const rt = this.idRenderTarget;
        const { ids } = await this.readIds(x, y, 1 / rt.width, 1 / rt.height);
        return ids[0];
    }

    // Read rectangle of splat IDs using normalized coordinates (0-1 range) (after
    // prepareId). The ids are row-major over the pixel rectangle actually read:
    // the request rounded outward to whole pixels of the render target at its
    // current size. Callers that index the ids as a grid must map pixels through
    // the returned rectangle and target size, not a size captured earlier
    async readIds(x: number, y: number, width: number, height: number): Promise<IdPick> {
        if (!this.idRenderTarget) {
            return { ids: [], x: 0, y: 0, width: 0, height: 0, targetWidth: 0, targetHeight: 0 };
        }

        const rt = this.idRenderTarget;
        const colorBuffer = rt.colorBuffer;

        // Convert normalized coordinates to render target pixels
        const px = Math.floor(x * rt.width);
        const py = Math.floor(y * rt.height);
        const pw = Math.max(1, Math.ceil((x + width) * rt.width) - px);
        const ph = Math.max(1, Math.ceil((y + height) * rt.height) - py);

        // Read pixels using texture.read() API. The read must be immediate: the
        // id pass is rendered synchronously by prepareId and nothing submits the
        // shared command encoder before the caller awaits us, so a deferred read
        // maps the staging buffer before the copy has run and returns zeros.
        const pixels = await colorBuffer.read(px, py, pw, ph, {
            renderTarget: rt,
            immediate: true
        });

        // Row 0 of the result is the top row of the requested rectangle.
        const result: number[] = [];
        for (let row = 0; row < ph; ++row) {
            const src = row * pw;
            for (let col = 0; col < pw; ++col) {
                const i = (src + col) * 4;
                // Use >>> 0 to convert signed 32-bit to unsigned (so 0xffffffff instead of -1)
                result.push(
                    (pixels[i] |
                    (pixels[i + 1] << 8) |
                    (pixels[i + 2] << 16) |
                    (pixels[i + 3] << 24)) >>> 0
                );
            }
        }

        return { ids: result, x: px, y: py, width: pw, height: ph, targetWidth: rt.width, targetHeight: rt.height };
    }

    // Prepare for depth picking by rendering the specified splat
    prepareDepth(splat: Splat) {
        if (!this.depthRenderTarget) {
            return;
        }

        const { scene } = this;
        const { app, camera, splatLayer } = scene;
        const emptyMap = new Map();

        // Hide non-selected elements
        const splats = scene.getElementsByType(ElementType.splat) as Splat[];
        splats.forEach((s) => {
            s.entity.enabled = s === splat;
        });

        // Set depth estimation mode uniform
        this.device.scope.resolve('pickOp').setValue(2); // 'set' mode - don't skip any visible splats
        this.device.scope.resolve('pickMode').setValue(1);
        scene.projectedSplatRenderer.preparePick(splat, 2, true, 1);

        // Render scene with depth pass
        this.renderPass.blendState = this.depthBlendState;
        this.renderPass.init(this.depthRenderTarget);
        this.renderPass.setClearColor(depthClearColor);
        this.renderPass.update(camera.camera, app.scene, [splatLayer], emptyMap, false);
        this.renderPass.render();
        scene.projectedSplatRenderer.finishPick();

        // Re-enable all splats
        splats.forEach((s) => {
            s.entity.enabled = true;
        });
    }

    // Read normalized depth (0-1) at normalized screen position (0-1 range) (after prepareDepth)
    async readDepth(x: number, y: number): Promise<number | null> {
        if (!this.depthRenderTarget) {
            return null;
        }

        const rt = this.depthRenderTarget;
        const colorBuffer = rt.colorBuffer;

        if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || x > 1 || y < 0 || y > 1 || rt.width < 1 || rt.height < 1) {
            return null;
        }

        // Convert normalized coordinates to render target pixels
        const px = Math.min(Math.floor(x * rt.width), rt.width - 1);
        const py = Math.min(Math.floor(y * rt.height), rt.height - 1);

        // Read the pixel using Texture.read() which handles RGBA16F format
        const pixels = await colorBuffer.read(px, py, 1, 1, {
            renderTarget: rt,
            immediate: true
        });

        return this.decodeDepth(pixels, 0);
    }

    // Read normalized depth at scattered screen positions. Points are grouped
    // into small tiles so a brush stroke needs a handful of readbacks instead
    // of one readback per sample or one large read of its whole screen bound.
    async readDepths(points: { x: number, y: number }[]): Promise<(number | null)[]> {
        if (!this.depthRenderTarget) {
            return new Array(points.length).fill(null);
        }

        const rt = this.depthRenderTarget;
        const pixelsX = new Int32Array(points.length);
        const pixelsY = new Int32Array(points.length);
        const result: (number | null)[] = new Array(points.length).fill(null);
        const tiles = new Map<string, { indices: number[], minX: number, minY: number, maxX: number, maxY: number }>();
        const tileSize = 64;

        for (let i = 0; i < points.length; ++i) {
            const { x, y } = points[i];
            if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || x > 1 || y < 0 || y > 1 || rt.width < 1 || rt.height < 1) {
                continue;
            }

            const px = Math.min(Math.floor(x * rt.width), rt.width - 1);
            const py = Math.min(Math.floor(y * rt.height), rt.height - 1);
            pixelsX[i] = px;
            pixelsY[i] = py;
            const key = `${Math.floor(px / tileSize)},${Math.floor(py / tileSize)}`;
            const tile = tiles.get(key);
            if (tile) {
                tile.indices.push(i);
                tile.minX = Math.min(tile.minX, px);
                tile.minY = Math.min(tile.minY, py);
                tile.maxX = Math.max(tile.maxX, px);
                tile.maxY = Math.max(tile.maxY, py);
            } else {
                tiles.set(key, { indices: [i], minX: px, minY: py, maxX: px, maxY: py });
            }
        }

        for (const tile of tiles.values()) {
            const width = tile.maxX - tile.minX + 1;
            const height = tile.maxY - tile.minY + 1;
            const pixels = await rt.colorBuffer.read(tile.minX, tile.minY, width, height, {
                renderTarget: rt,
                immediate: true
            });

            for (const index of tile.indices) {
                const offset = ((pixelsY[index] - tile.minY) * width + pixelsX[index] - tile.minX) * 4;
                result[index] = this.decodeDepth(pixels, offset);
            }
        }

        return result;
    }

    private decodeDepth(pixels: any, offset: number): number | null {
        // R channel: accumulated depth * alpha
        // A channel: transmittance (1 - alpha)
        const r = half2Float(pixels[offset]);
        const transmittance = half2Float(pixels[offset + 3]);
        const alpha = 1 - transmittance;

        // Check alpha (transmittance close to 1 means nothing visible)
        if (alpha < 1e-6) {
            return null;
        }

        // Return normalized depth (0-1 range)
        return r / alpha;
    }

    // Clean up resources
    destroy() {
        this.renderPass?.destroy();
    }
}

export { Picker };
